import { MAX_PER_SIDE, POCKET_MAX } from './citygen.js';
import { CODE_DEFAULTS, tuningLeaves } from './tuning.js';
import { VETERAN_DEVELOPMENT_SKILLS } from './botDevelopment.js';
// ---------------------------------------------------------------------------
// Scenarios: experiments described as data (scenarios/*.json) and played
// headlessly through the real engine and bots.
//
// A series plays the same match-up across a list of values for one setting —
// the smaller side's city count, a skill, the round cap, or a tuning value —
// with a run of seeds at each, and summarises who won. The runner (scripts/scenario.mjs) spreads
// the matches over worker threads and plays them on the server's engine
// (packages/server/src/scenario.ts); everything here is pure, so the lab page
// checks a scenario as it is edited and reads the same summary.
// ---------------------------------------------------------------------------
export const SKILLS = ['RECRUIT', 'REGULAR', 'VETERAN', 'MASTER', 'MASTER_PAIR', 'JEV_SMART', 'JEV_SMARTER', 'JEV_SMARTEST', 'VETERAN_OFFENSIVE', 'VETERAN_NOSHELTER', 'VETERAN_INDUSTRY',
    'RECRUIT_FILL', 'REGULAR_FILL', 'VETERAN_FILL', ...VETERAN_DEVELOPMENT_SKILLS];
/**
 * The commanders that ask TypeSafe's Jev for their strategy each phase (D35):
 * offered in the simulation room and the labs only, since every phase is a
 * paid call, and refused anywhere else.
 */
export const JEV_SKILLS = ['JEV_SMART', 'JEV_SMARTER', 'JEV_SMARTEST'];
export const isJevSkill = (skill) => JEV_SKILLS.includes(skill);
/** The endgame policies a side can be given (SkillProfile.endgame). */
export const ENDGAMES = ['FINISH', 'STALL', 'ADAPTIVE'];
/** The deficits the fell-behind curve is read at, in standing cities. */
export const FELL_BEHIND_BY = [1, 2, 3, 4, 6, 8];
const list = (v) => (Array.isArray(v) ? v : [v]);
/** Everything wrong with a scenario, or an empty list. */
export function validateScenario(s) {
    const errs = [];
    if (typeof s !== 'object' || s === null)
        return ['a scenario is a JSON object'];
    const x = s;
    if (typeof x.id !== 'string' || !/^[a-z0-9-]+$/.test(x.id))
        errs.push('id: lower-case letters, digits and hyphens');
    if (x.kind !== 'series')
        errs.push('kind: "series"');
    if (typeof x.title !== 'string' || !x.title)
        errs.push('title: a short name');
    const count = (v, where) => {
        for (const n of list(v)) {
            if (!Number.isInteger(n) || n < 1 || n > MAX_PER_SIDE)
                errs.push(`${where}: whole numbers from 1 to ${MAX_PER_SIDE}`);
        }
    };
    const profile = (v, where) => {
        if (v === undefined)
            return;
        if (typeof v !== 'object' || v === null || Array.isArray(v)) {
            errs.push(`${where}: an object of NPC levers`);
            return;
        }
        for (const [k, x] of Object.entries(v)) {
            if (!['number', 'boolean', 'string'].includes(typeof x))
                errs.push(`${where}.${k}: a number, true/false or a word`);
            if (k === 'endgame' && !ENDGAMES.includes(x))
                errs.push(`${where}.endgame: one of ${ENDGAMES.join(', ')}`);
        }
    };
    const skill = (v, where) => {
        for (const k of list(v)) {
            if (!SKILLS.includes(k))
                errs.push(`${where}: one of ${SKILLS.join(', ')}`);
        }
    };
    if (!x.sides?.a || !x.sides?.b)
        errs.push('sides: an "a" and a "b"');
    else {
        count(x.sides.a.cities, 'sides.a.cities');
        if (!x.mirror)
            count(x.sides.b.cities, 'sides.b.cities');
        skill(x.sides.a.skill, 'sides.a.skill');
        if (!x.mirror)
            skill(x.sides.b.skill, 'sides.b.skill');
        profile(x.sides.a.profile, 'sides.a.profile');
        profile(x.sides.b.profile, 'sides.b.profile');
    }
    if (!Number.isInteger(x.seeds) || x.seeds < 1 || x.seeds > 400)
        errs.push('seeds: 1 to 400');
    for (const r of list(x.rounds ?? []))
        if (!Number.isInteger(r) || r < 1 || r > 200)
            errs.push('rounds: 1 to 200');
    if (x.rounds === undefined)
        errs.push('rounds: the round cap');
    if (x.headline !== undefined && x.headline !== 'a' && x.headline !== 'b')
        errs.push('headline: "a" or "b"');
    if (x.battle !== undefined && typeof x.battle !== 'boolean')
        errs.push('battle: true or false');
    if (x.battle && x.sides?.a && x.sides?.b && [x.sides.a.cities, x.mirror ? POCKET_MAX : x.sides.b.cities].some((n) => n !== POCKET_MAX)) {
        errs.push(`battle: a battle is ${POCKET_MAX} cities a side`);
    }
    if (x.tuning !== undefined && (typeof x.tuning !== 'object' || x.tuning === null || Array.isArray(x.tuning))) {
        errs.push('tuning: an object of values');
    }
    if (x.vary !== undefined) {
        const v = x.vary;
        const leaves = tuningLeaves(CODE_DEFAULTS);
        if (typeof v.path !== 'string' || typeof leaves.get(v.path) !== 'number') {
            errs.push('vary.path: a number in tuning.json, by its dotted path, e.g. RESILIENCE_EXP or CONVERSION.keep');
        }
        if (!Array.isArray(v.values) || v.values.length === 0 || !v.values.every((n) => typeof n === 'number' && Number.isFinite(n))) {
            errs.push('vary.values: a list of numbers');
        }
    }
    if (errs.length === 0) {
        const lists = axesOf(x);
        if (lists.length > 1)
            errs.push(`only one setting may be a list; ${lists.join(' and ')} both are`);
    }
    return errs;
}
function axesOf(s) {
    const out = [];
    if (s.vary)
        out.push('tuning');
    if (Array.isArray(s.sides.a.cities))
        out.push('a.cities');
    if (Array.isArray(s.sides.b.cities) && !s.mirror)
        out.push('b.cities');
    if (Array.isArray(s.sides.a.skill))
        out.push('a.skill');
    if (Array.isArray(s.sides.b.skill) && !s.mirror)
        out.push('b.skill');
    if (Array.isArray(s.rounds))
        out.push('rounds');
    return out;
}
/** The setting the series varies, and its values. A scenario with no list is one step. */
export function seriesAxis(s) {
    const [axis] = axesOf(s);
    switch (axis) {
        case 'tuning': return { axis, values: s.vary.values, path: s.vary.path };
        case 'a.cities': return { axis, values: s.sides.a.cities };
        case 'b.cities': return { axis, values: s.sides.b.cities };
        case 'a.skill': return { axis, values: s.sides.a.skill };
        case 'b.skill': return { axis, values: s.sides.b.skill };
        case 'rounds': return { axis, values: s.rounds };
        default: return { axis: 'b.cities', values: [s.sides.b.cities] };
    }
}
/** Every match the scenario plays, in order. Seeds are fixed, so a rerun plays the same matches. */
export function expandScenario(s) {
    const { axis, values, path } = seriesAxis(s);
    const jobs = [];
    const swap = s.swapSeats !== false;
    values.forEach((value, step) => {
        const pick = (field, v) => (axis === field ? value : v);
        for (let index = 0; index < s.seeds; index++) {
            const map = swap ? Math.floor(index / 2) : index;
            jobs.push({
                step,
                index,
                // Pairs share a map: one from each seat. A tuning series plays the
                // same maps at every value, so only the lever differs.
                seed: axis === 'tuning' ? `${s.id}:${map}` : `${s.id}:${value}:${map}`,
                aIsWest: swap ? index % 2 === 0 : true,
                aCities: pick('a.cities', s.sides.a.cities),
                bCities: s.mirror ? pick('a.cities', s.sides.a.cities) : pick('b.cities', s.sides.b.cities),
                aSkill: pick('a.skill', s.sides.a.skill),
                bSkill: s.mirror ? pick('a.skill', s.sides.a.skill) : pick('b.skill', s.sides.b.skill),
                rounds: pick('rounds', s.rounds),
                ...(axis === 'tuning' ? { set: { path: path, value: value } } : {}),
                ...(s.sides.a.profile ? { aProfile: s.sides.a.profile } : {}),
                ...((s.mirror ? s.sides.a.profile : s.sides.b.profile) ? { bProfile: (s.mirror ? s.sides.a.profile : s.sides.b.profile) } : {}),
                ...(s.battle ? { battle: true } : {}),
            });
        }
    });
    return jobs;
}
/**
 * The Wilson interval for k successes in n, at 90%. Sixteen matches is a small
 * sample: one in sixteen reads as 6%, but the truth could be anything from
 * about 1% to 22%, and the table should say so.
 */
export function wilson(k, n, z = 1.645) {
    if (n === 0)
        return { low: 0, high: 1 };
    const p = k / n;
    const d = 1 + (z * z) / n;
    const centre = (p + (z * z) / (2 * n)) / d;
    const half = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / d;
    return { low: Math.max(0, centre - half), high: Math.min(1, centre + half) };
}
const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
/** Of the matches where `inTrouble` names a side, how many that side won. */
function comeback(rows, inTrouble) {
    let played = 0;
    let won = 0;
    for (const r of rows) {
        const side = inTrouble(r);
        if (!side)
            continue;
        played++;
        if (r.winner === side)
            won++;
    }
    return { played, won };
}
/**
 * The regional and strategic layer over a set of matches (TelemetrySummary).
 * Null when none of them carries telemetry.
 */
export function telemetrySummary(rows) {
    const runs = rows.filter((r) => r.telemetry && r.telemetry.rounds.length > 0);
    if (runs.length === 0)
        return null;
    const FIFTHS = 5;
    const bins = Array.from({ length: FIFTHS }, () => ({
        n: 0, eligible: 0, regional: 0, regionalActive: 0, regionalRear: 0,
        launches: 0, launchesActive: 0, launchesRear: 0, icbm: 0, local: 0,
    }));
    const byRound = new Map();
    const stageLen = new Map();
    const er = [];
    const on = { ACTIVE: 0, RESOLVED: 0, RESERVE: 0 };
    const clusters = { decided: 0, helped: 0, tipped: 0 };
    const kills = { regional: 0, icbm: 0 };
    let supply = 0;
    let supplyOut = 0;
    const snow = { reached: 0, leaderWon: 0, lead: [0, 0, 0] };
    const behind = FELL_BEHIND_BY.map((by) => ({ by, matches: 0, won: 0, rounds: 0 }));
    for (const r of runs) {
        const t = r.telemetry;
        // Each stage's first and last round, finished or not.
        const span = new Map();
        for (const x of t.rounds) {
            const s = span.get(x.stage) ?? { from: x.round, to: x.round };
            s.from = Math.min(s.from, x.round);
            s.to = Math.max(s.to, x.round);
            span.set(x.stage, s);
        }
        const last = Math.max(...t.rounds.map((x) => x.stage));
        for (const [stage, s] of span) {
            if (stage === last && r.winner == null)
                continue;
            stageLen.set(stage, [...(stageLen.get(stage) ?? []), s.to - s.from + 1]);
        }
        for (const side of ['A', 'B']) {
            const first = t.rounds.find((x) => x[`extended${side}`] > 0);
            if (first)
                er.push(first.round);
        }
        for (const x of t.rounds) {
            const s = span.get(x.stage);
            const f = Math.min(FIFTHS - 1, Math.floor(((x.round - s.from) / (s.to - s.from + 1)) * FIFTHS));
            const b = bins[f];
            const launches = x.regionalA + x.regionalB;
            b.n++;
            b.eligible += x.eligibleA + x.eligibleB;
            b.launches += launches;
            b.launchesActive += x.regionalOn.ACTIVE;
            b.launchesRear += x.regionalOn.RESOLVED + x.regionalOn.RESERVE;
            b.regional += x.damage.regional;
            b.regionalActive += x.damage.regionalActive;
            b.regionalRear += x.damage.regionalRear;
            b.icbm += x.damage.icbm;
            b.local += x.damage.local;
            const c = byRound.get(x.round) ?? { n: 0, regional: 0, icbm: 0 };
            c.n++;
            c.regional += x.damage.regional;
            c.icbm += x.damage.icbm;
            byRound.set(x.round, c);
            on.ACTIVE += x.regionalOn.ACTIVE;
            on.RESOLVED += x.regionalOn.RESOLVED;
            on.RESERVE += x.regionalOn.RESERVE;
            kills.regional += x.kills.regional;
            kills.icbm += x.kills.icbm;
            supply += x.supply;
            supplyOut += x.supplyOutnumbered;
        }
        for (const c of t.clusters) {
            if (!c.heldBy)
                continue;
            clusters.decided++;
            if (c.helped)
                clusters.helped++;
            if (c.tipped)
                clusters.tipped++;
        }
        // Falling behind in play: only from an even start, where nobody began with spares.
        if (r.aCities === r.bCities) {
            for (const b of behind) {
                const x = t.rounds.find((y) => Math.abs(y.liveA - y.liveB) >= b.by);
                if (!x)
                    continue;
                b.matches++;
                b.rounds += x.round;
                if (r.winner === (x.liveA < x.liveB ? 'a' : 'b'))
                    b.won++;
            }
        }
        // The first side to lead R(t) by two: did it win, and how did its lead grow?
        const at = t.rounds.findIndex((x) => Math.abs(x.resolvedA - x.resolvedB) >= 2);
        if (at >= 0) {
            const x = t.rounds[at];
            const side = x.resolvedA > x.resolvedB ? 'a' : 'b';
            const lead = (y) => (side === 'a' ? y.liveA - y.liveB : y.liveB - y.liveA);
            const later = (k) => t.rounds[Math.min(t.rounds.length - 1, at + k)];
            snow.reached++;
            if (r.winner === side)
                snow.leaderWon++;
            snow.lead[0] += lead(x);
            snow.lead[1] += lead(later(5));
            snow.lead[2] += lead(later(10));
        }
    }
    const per = (a, b) => (b > 0 ? a / b : 0);
    const crossover = [...byRound.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([round, c]) => ({ round, regional: per(c.regional, c.n), icbm: per(c.icbm, c.n) }));
    // From here on, ICBMs out-damage regional ballistics every round with any of either.
    let crossoverRound = null;
    for (let i = crossover.length - 1; i >= 0; i--) {
        const c = crossover[i];
        if (c.regional + c.icbm === 0)
            continue;
        if (c.icbm > c.regional)
            crossoverRound = c.round;
        else
            break;
    }
    const regionalLaunches = on.ACTIVE + on.RESOLVED + on.RESERVE;
    return {
        matches: runs.length,
        stageRounds: [...stageLen.entries()].sort((a, b) => a[0] - b[0]).map(([, xs]) => mean(xs)),
        erRound: er.length ? mean(er) : null,
        byStageFifth: bins.map((b) => ({
            eligible: per(b.eligible, b.n),
            use: per(b.launches, b.eligible),
            effectActive: per(b.regionalActive, b.launchesActive),
            effectRear: per(b.regionalRear, b.launchesRear),
            regional: per(b.regional, b.n),
            icbm: per(b.icbm, b.n),
            local: per(b.local, b.n),
        })),
        crossover,
        crossoverRound,
        regionalOn: {
            ACTIVE: per(on.ACTIVE, regionalLaunches),
            RESOLVED: per(on.RESOLVED, regionalLaunches),
            RESERVE: per(on.RESERVE, regionalLaunches),
        },
        clusters,
        kills,
        supplyOutnumbered: per(supplyOut, supply),
        snowball: {
            reached: snow.reached,
            leaderWon: snow.leaderWon,
            lead: snow.lead.map((x) => per(x, snow.reached)),
        },
        fellBehind: behind.map((b) => ({ by: b.by, matches: b.matches, won: b.won, round: per(b.rounds, b.matches) })),
    };
}
export function summarise(s, rows) {
    const { axis, values } = seriesAxis(s);
    const jobs = expandScenario(s);
    const headline = s.headline ?? 'b';
    const steps = values.map((value, step) => {
        const at = rows.filter((r) => r.step === step);
        const first = jobs.find((j) => j.step === step);
        const wins = at.filter((r) => r.winner === headline).length;
        const draws = at.filter((r) => r.winner == null);
        const { low, high } = wilson(wins, at.length);
        return {
            value,
            aCities: first.aCities,
            bCities: first.bCities,
            aSkill: first.aSkill,
            bSkill: first.bSkill,
            played: at.length,
            aWins: at.filter((r) => r.winner === 'a').length,
            bWins: at.filter((r) => r.winner === 'b').length,
            draws: draws.length,
            rate: at.length ? wins / at.length : 0,
            low,
            high,
            aheadAtCap: draws.filter((r) => (headline === 'a' ? r.aHealth - r.bHealth : r.bHealth - r.aHealth) > 0).length,
            meanRounds: mean(at.map((r) => r.rounds)),
            aLeft: mean(at.map((r) => r.aLeft)),
            bLeft: mean(at.map((r) => r.bLeft)),
            aHealth: mean(at.map((r) => r.aHealth)),
            bHealth: mean(at.map((r) => r.bHealth)),
            meanStages: mean(at.map((r) => r.stages ?? 1)),
            lostFirstCluster: comeback(at, (r) => (r.firstCluster ? (r.firstCluster === 'a' ? 'b' : 'a') : null)),
            behindAfterStage1: comeback(at, (r) => {
                const s1 = r.afterStage1;
                if (!s1 || s1.a === s1.b)
                    return null;
                return s1.a < s1.b ? 'a' : 'b';
            }),
            westWon: comeback(at.filter((r) => r.winner), (r) => (r.aIsWest ? 'a' : 'b')),
            telemetry: telemetrySummary(at),
        };
    });
    // How far behind the headline side started, where it did.
    const deficit = (x) => (headline === 'b' ? x.aCities - x.bCities : x.bCities - x.aCities);
    const behind = steps.filter((x) => deficit(x) > 0 && x.played > 0);
    const best = (ok) => {
        const won = behind.filter(ok).map(deficit);
        return won.length ? Math.max(...won) : null;
    };
    return {
        axis,
        ...(axis === 'tuning' ? { path: s.vary.path } : {}),
        headline,
        steps,
        overturned: axis === 'a.cities' || axis === 'b.cities'
            ? {
                once: best((x) => (headline === 'b' ? x.bWins : x.aWins) > 0),
                quarter: best((x) => x.rate >= 0.25),
            }
            : null,
    };
}
//# sourceMappingURL=scenario.js.map